import classNames from 'classnames/bind';
import * as React from 'react';
import styles from './typography.css';

const cx = classNames.bind(styles);

type AllowedTags = 'h1' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
interface TypographyComponentProps {
  children: React.ReactNode;
  lineClamp?: number;
  tag?: AllowedTags;
  color?:
    | 'inherit'
    | 'lightGrey1'
    | 'lightGrey2'
    | 'lightGrey3'
    | 'grey'
    | 'purple'
    | 'red'
    | 'green'
    | 'white'
    | 'black'
    | 'midnightBlue'
    | 'gradient';
  align?: 'left' | 'center' | 'right';
  selectable?: boolean;
  preWrap?: boolean;
  wordBreak?: 'break-all' | 'break-word';
  // When `true` all numbers will have the same width.
  tabularNums?: boolean;
}

function makeTypographyComponent(
  tagClassName: string,
  DefaultTag: AllowedTags,
): React.ComponentType<TypographyComponentProps> {
  return function Typography({
    children,
    lineClamp,
    tag = DefaultTag,
    color = 'inherit',
    align,
    selectable,
    preWrap,
    wordBreak,
    tabularNums,
  }: TypographyComponentProps) {
    const className = cx(
      styles.base,
      tagClassName,
      lineClamp && styles.lineClamp,
      color == 'inherit'
        ? null
        : color === 'gradient'
        ? styles.gradient
        : color,
      preWrap && styles.preWrap,
      wordBreak === 'break-all' && styles.breakAll,
      wordBreak === 'break-word' && styles.breakWord,
      tabularNums && styles.tabularNums,
    );
    const style = {
      WebkitLineClamp: lineClamp,
      textAlign: align,
      userSelect: selectable ? 'text' : undefined,
    };
    return React.createElement(tag, { className, style }, children);
  };
}

export const TextMedium = makeTypographyComponent(styles.textMedium, 'p');
export const TextMediumLight = makeTypographyComponent(
  styles.textMediumLight,
  'p',
);
export const TextRegular = makeTypographyComponent(styles.textRegular, 'p');
export const TextRegularLight = makeTypographyComponent(
  styles.textRegularLight,
  'p',
);
export const TextSmall = makeTypographyComponent(styles.textSmall, 'p');
export const TextSmallLight = makeTypographyComponent(
  styles.textSmallLight,
  'p',
);
export const TextTiny = makeTypographyComponent(styles.textTiny, 'p');
export const TextTinyLight = makeTypographyComponent(styles.textTinyLight, 'p');
export const TextXTiny = makeTypographyComponent(styles.textXTiny, 'p');

export const TextSpecialSectionName = makeTypographyComponent(
  styles.textSpecialSectionName,
  'p',
);
export const TextSpecialSectionDescription = makeTypographyComponent(
  styles.textSpecialSectionDescription,
  'p',
);

export const TitlePricing = makeTypographyComponent(styles.titlePricing, 'h1');
export const Title00 = makeTypographyComponent(styles.title00, 'h1');
export const Title0 = makeTypographyComponent(styles.title0, 'h1');
export const Title1 = makeTypographyComponent(styles.title1, 'h1');
export const Title3 = makeTypographyComponent(styles.title3, 'h3');
export const Title4 = makeTypographyComponent(styles.title4, 'h4');
export const Title5 = makeTypographyComponent(styles.title5, 'h5');
export const Title6 = makeTypographyComponent(styles.title6, 'h6');
